import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import Logo from './Logo';

/**
 * SimplifiedHeader Component
 *
 * Distraction-free header for focused workflows (wizards, checkout, etc.)
 * Reuses the standard Logo component to maintain consistency.
 *
 * Used in:
 * - Experience Creation Wizard (SCRUM-342)
 * - Booking Flow (SCRUM-338)
 *
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.backUrl - URL to navigate to on back button click
 * @param {Function} props.onBack - Optional custom back handler (overrides backUrl)
 * @param {Function} props.onClose - Optional close handler (shows X button)
 * @param {boolean} props.showLogo - Show logo (default: true)
 * @param {React.ReactNode} props.rightContent - Optional content for right side
 */
export default function SimplifiedHeader({
  title,
  backUrl,
  onBack,
  onClose,
  showLogo = true,
  rightContent = null
}) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backUrl) {
      navigate(backUrl);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left: Back button or Logo */}
          <div className="flex items-center gap-4 min-w-0">
            {(backUrl || onBack) && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition flex-shrink-0"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium hidden sm:inline">Back</span>
              </button>
            )}

            {showLogo && (
              <div className={backUrl || onBack ? 'hidden md:block' : 'block'}>
                <Logo />
              </div>
            )}
          </div>

          {/* Center: Title */}
          {title && (
            <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate px-4">
              {title}
            </h1>
          )}

          {/* Right: Close button or custom content */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {rightContent}

            {onClose && (
              <button
                onClick={handleClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            )}

            {/* Spacer to keep title centered when no close button */}
            {!onClose && !rightContent && (backUrl || onBack) && (
              <div className="w-20"></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
